import { Box, Typography, TextField, Button, Grid, Paper, IconButton, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Select } from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";
import { useAuth } from "../../../context/AuthContext";
import api from "../../../services/api";
import SupplierForm from "../../inventory/components/SupplierForm";

interface Item { id: string; name: string; quantity: number; cost: number; }
interface Supplier { _id: string; name: string; }
type PurchaseRow = { id: string; source: 'po'|'manual'; date: string; supplierId?: string; supplier: string; itemsCount: number; total: number; paid: number; status: 'paid'|'partial'|'unpaid'; orderNumber?: string };

export default function PurchasesPage() {
  useProtectedRoute();
  const { token } = useAuth();

  const [supplier, setSupplier] = useState("");
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [items, setItems] = useState<Item[]>([{ id: "", name: "", quantity: 1, cost: 0 }]);
  const [purchases, setPurchases] = useState<PurchaseRow[]>([]);
  const [search, setSearch] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [payTarget, setPayTarget] = useState<PurchaseRow | null>(null);
  const [payAmount, setPayAmount] = useState<number>(0);
  const [payMethod, setPayMethod] = useState<string>('bank');

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await api.get("/inventory/suppliers/all", { headers: { Authorization: `Bearer ${token}` } });
      setSuppliers(res.data);
    } catch (err) { console.error("Error loading suppliers", err); }
  }, [token]);

  const fetchPurchases = useCallback(async () => {
    try {
      const res = await api.get("/purchases", { headers: { Authorization: `Bearer ${token}` } });
      setPurchases(res.data);
    } catch (err) { console.error("Error fetching purchases", err); }
  }, [token]);

  useEffect(() => { fetchSuppliers(); fetchPurchases(); }, [fetchSuppliers, fetchPurchases]);

  const addItem = () => setItems([...items, { id: "", name: "", quantity: 1, cost: 0 }]);
  const updateItem = (index: number, field: keyof Item, value: string | number) => {
    const updated = [...items]; const item = { ...updated[index] } as any;
    if (field === 'quantity' || field === 'cost') { const v = parseFloat(value as string); item[field] = isNaN(v) ? 0 : v; } else { item[field] = value as string; }
    updated[index] = item; setItems(updated);
  };
  const removeItem = (index: number) => setItems(items.filter((_, i) => i !== index));
  const subtotal = useMemo(() => +items.reduce((s, it) => s + (it.quantity||0) * (it.cost||0), 0).toFixed(2), [items]);
  const tax = +(subtotal * 0.15).toFixed(2);
  const total = +(subtotal + tax).toFixed(2);
  const isFormValid = () => supplier && items.every(it => it.id && it.name && it.quantity > 0 && it.cost >= 0);

  const handleSubmit = async () => {
    if (!isFormValid()) { alert("Please fill in all fields correctly."); return; }
    try {
      const payload = { supplierId: supplier, items: items.map(i => ({ itemId: i.id, name: i.name, quantity: i.quantity, costPrice: i.cost, total: i.quantity * i.cost })), subtotal, tax, totalAmount: total };
      await api.post('/purchases/add', payload, { headers: { Authorization: `Bearer ${token}` } });
      alert('Purchase recorded.'); setSupplier(''); setItems([{ id: "", name: "", quantity: 1, cost: 0 }]); fetchPurchases();
    } catch (err) { alert('Error saving purchase.'); console.error(err); }
  };

  const filteredPurchases = purchases.filter(p => p.supplier?.toLowerCase().includes(search.toLowerCase()));
  const openPayDialog = (row: PurchaseRow) => { setPayTarget(row); const outstanding = Math.max(0, row.total - row.paid); setPayAmount(+outstanding.toFixed(2)); setPayMethod('bank'); setPayOpen(true); };
  const submitPayment = async () => { if (!payTarget) return; try { await api.post('/purchases/pay', { amount: payAmount, method: payMethod, supplierId: payTarget.supplierId, poId: payTarget.id }, { headers: { Authorization: `Bearer ${token}` } }); setPayOpen(false); setPayTarget(null); fetchPurchases(); } catch (e: any) { alert(e?.response?.data?.message || 'Payment failed'); } };

  const handleSupplierCreated = async () => { setOpenModal(false); await fetchSuppliers(); };
  const handleExport = (type: 'csv' | 'pdf') => { alert(`Export to ${type.toUpperCase()} coming soon.`); };

  return (
    <Box p={4}>
      <Typography variant="h5" fontWeight="bold" mb={2}>Record Purchase</Typography>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6}>
          <Select fullWidth displayEmpty value={supplier} onChange={(e) => setSupplier(e.target.value as string)}>
            <MenuItem value=""><em>Select Supplier</em></MenuItem>
            {suppliers.map(s => (<MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>))}
          </Select>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Button variant="outlined" onClick={() => setOpenModal(true)}>Add New Supplier</Button>
        </Grid>
      </Grid>

      {items.map((item, index) => (
        <Paper key={index} className="p-4 mb-3">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}><TextField label="Item ID" value={item.id} onChange={(e) => updateItem(index, 'id', e.target.value)} fullWidth /></Grid>
            <Grid item xs={12} sm={3}><TextField label="Item Name" value={item.name} onChange={(e) => updateItem(index, 'name', e.target.value)} fullWidth /></Grid>
            <Grid item xs={6} sm={2}><TextField label="Qty" type="number" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', e.target.value)} fullWidth /></Grid>
            <Grid item xs={6} sm={2}><TextField label="Cost" type="number" value={item.cost} onChange={(e) => updateItem(index, 'cost', e.target.value)} fullWidth /></Grid>
            <Grid item xs={1} display="flex" alignItems="center"><IconButton onClick={() => removeItem(index)} color="error"><Delete /></IconButton></Grid>
          </Grid>
        </Paper>
      ))}

      <Button variant="outlined" startIcon={<Add />} onClick={addItem} sx={{ mb: 2 }}>Add Item</Button>
      <Box mb={3}><Typography>Subtotal: ${subtotal}</Typography><Typography>Tax (15%): ${tax}</Typography><Typography fontWeight="bold">Total: ${total}</Typography></Box>
      <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!isFormValid()}>Submit Purchase</Button>

      <Box mt={5} mb={2} display="flex" alignItems="center" gap={2}>
        <TextField label="Search by Supplier" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Button variant="outlined" onClick={() => handleExport('csv')}>Export CSV</Button>
        <Button variant="outlined" onClick={() => handleExport('pdf')}>Export PDF</Button>
      </Box>

      <Typography variant="h6" mb={2}>All Purchases</Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Items</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Paid</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPurchases.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{new Date(row.date).toLocaleDateString()}</TableCell>
                <TableCell>{row.source === 'po' ? (row.orderNumber || 'PO') : 'Manual'}</TableCell>
                <TableCell>{row.supplier}</TableCell>
                <TableCell>{row.itemsCount}</TableCell>
                <TableCell>${row.total.toFixed(2)}</TableCell>
                <TableCell>${row.paid.toFixed(2)}</TableCell>
                <TableCell>{row.status.toUpperCase()}</TableCell>
                <TableCell>{row.status !== 'paid' && (<Button size="small" variant="outlined" onClick={() => openPayDialog(row)}>Pay</Button>)}</TableCell>
              </TableRow>
            ))}
            {filteredPurchases.length === 0 && (<TableRow><TableCell colSpan={8} align="center">No purchases found.</TableCell></TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Supplier</DialogTitle>
        <DialogContent><SupplierForm onSubmit={handleSupplierCreated} saving={false} /></DialogContent>
        <DialogActions><Button onClick={() => setOpenModal(false)}>Cancel</Button></DialogActions>
      </Dialog>

      <Dialog open={payOpen} onClose={() => setPayOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Pay Supplier</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Typography variant="body2">{payTarget?.supplier}</Typography>
            <TextField label="Amount" type="number" value={payAmount} onChange={(e) => setPayAmount(Number(e.target.value))} inputProps={{ min: 0, step: 0.01 }} fullWidth />
            <Select fullWidth value={payMethod} onChange={(e) => setPayMethod(e.target.value as string)}>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="bank">Bank</MenuItem>
              <MenuItem value="mobile">Mobile Money</MenuItem>
            </Select>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayOpen(false)}>Cancel</Button>
          <Button onClick={submitPayment} variant="contained">Pay</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
